import { useState, useEffect, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import DOMPurify from "dompurify";
import {
  ArrowLeft,
  Clock,
  Users,
  FileText,
  Download,
  ChevronDown,
  ChevronUp,
  AlertTriangle,
  Check,
  Database,
  BookOpen,
  FileDown,
  Loader2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { api } from "@/App";

// Sanitize HTML content to prevent XSS attacks
const sanitizeHtml = (html) => {
  if (!html) return "";
  return DOMPurify.sanitize(html, { USE_PROFILES: { html: true } });
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ProtocolDetail() {
  const { slug } = useParams();
  const [protocol, setProtocol] = useState(null);
  const [pillar, setPillar] = useState(null);
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    roles: true,
    steps: true,
    documentation: false,
    data: false,
    notes: false,
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const protocolRes = await api.get(`/protocols/${slug}`);
        setProtocol(protocolRes.data);
        
        // Fetch pillar
        if (protocolRes.data.pillar_id) {
          const pillarsRes = await api.get("/pillars");
          const foundPillar = pillarsRes.data.find(
            (p) => p.id === protocolRes.data.pillar_id
          );
          setPillar(foundPillar);
        }
        
        // Fetch assets if any
        if (protocolRes.data.asset_ids?.length > 0) {
          const assetsRes = await api.get("/assets");
          const protocolAssets = assetsRes.data.filter(
            (a) => protocolRes.data.asset_ids.includes(a.id)
          );
          setAssets(protocolAssets);
        }
      } catch (error) {
        // Error fetching protocol data
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [slug]);

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const trackDownload = useCallback(async (assetId, assetTitle) => {
    try {
      await api.post("/analytics/event", {
        event_type: "asset_download",
        resource_id: assetId,
        resource_type: "asset",
        metadata: { protocol_id: protocol?.id, protocol_title: protocol?.title },
      });
      toast.success(`Downloaded: ${assetTitle}`);
    } catch (error) {
      // Error tracking download - silent fail
    }
  }, [protocol?.id, protocol?.title]);

  const downloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      // Track the download
      await api.post("/analytics/event", {
        event_type: "protocol_download",
        resource_id: protocol?.id,
        resource_type: "protocol",
        metadata: { format: "pdf" },
      });
      
      // Download the PDF
      const response = await fetch(`${BACKEND_URL}/api/protocols/${slug}/pdf`);
      if (!response.ok) {
        throw new Error("Failed to generate PDF");
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `protocol_${slug}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success("PDF downloaded successfully!");
    } catch (error) {
      toast.error("Failed to download PDF");
    } finally {
      setDownloadingPdf(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!protocol) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-slate-900 mb-4">Protocol Not Found</h1>
          <Link to="/protocols">
            <Button>Back to Protocols</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="protocol-detail-page">
      {/* Header */}
      <section className="bg-slate-900 text-white py-12 lg:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link 
            to="/protocols" 
            className="inline-flex items-center text-slate-400 hover:text-white mb-6"
            data-testid="back-to-protocols"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Protocols
          </Link>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <span className={`badge-urgency-${protocol.urgency} px-4 py-1.5 rounded-full text-sm font-mono uppercase`}>
                {protocol.urgency}
              </span>
              {pillar && (
                <Link 
                  to={`/framework/${pillar.slug}`}
                  className="text-sm text-slate-400 hover:text-white"
                >
                  {pillar.title}
                </Link>
              )}
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4" data-testid="protocol-title">
              {protocol.title}
            </h1>
            <p className="text-lg text-slate-300 max-w-3xl">
              {protocol.summary}
            </p>
            
            {/* Meta */}
            <div className="flex flex-wrap items-center gap-4 mt-6">
              <div className="flex flex-wrap gap-4 text-sm text-slate-400">
                <span className="flex items-center gap-2">
                  <BookOpen className="w-4 h-4" />
                  {protocol.applicable_settings?.join(", ")}
                </span>
                <span className="flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  {protocol.applicable_groups?.join(", ")}
                </span>
                <span className="flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  {protocol.steps?.length || 0} steps
                </span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-12 lg:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-8">
              {/* Triggers */}
              <div className="bg-amber-50 border border-amber-200 p-6 rounded-sm">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0" />
                  <div>
                    <h2 className="text-lg font-bold text-slate-900 mb-2">
                      When to Use This Protocol
                    </h2>
                    <div 
                      className="text-slate-700 prose prose-sm max-w-none" 
                      data-testid="protocol-triggers"
                      dangerouslySetInnerHTML={{ __html: sanitizeHtml(protocol.triggers) }}
                    />
                  </div>
                </div>
              </div>

              {/* Roles & Responsibilities */}
              <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
                <button
                  onClick={() => toggleSection("roles")}
                  className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-slate-50"
                  data-testid="toggle-roles"
                >
                  <div className="flex items-center gap-3">
                    <Users className="w-5 h-5 text-slate-500" />
                    <h2 className="text-lg font-bold text-slate-900">
                      Roles & Responsibilities
                    </h2>
                  </div>
                  {expandedSections.roles ? (
                    <ChevronUp className="w-5 h-5 text-slate-400" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-slate-400" />
                  )}
                </button>
                {expandedSections.roles && (
                  <div className="px-6 pb-6 space-y-4" data-testid="roles-content">
                    {protocol.roles?.map((role) => (
                      <div key={role.role_name} className="border-l-4 border-slate-200 pl-4">
                        <h3 className="font-bold text-slate-900 mb-2">
                          {role.role_name}
                        </h3>
                        <ul className="space-y-1">
                          {role.responsibilities.map((resp, i) => (
                            <li key={`${role.role_name}-resp-${i}`} className="flex items-start gap-2 text-sm text-slate-600">
                              <Check className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                              {resp}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Steps */}
              <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
                <button
                  onClick={() => toggleSection("steps")}
                  className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-slate-50"
                  data-testid="toggle-steps"
                >
                  <div className="flex items-center gap-3">
                    <Clock className="w-5 h-5 text-slate-500" />
                    <h2 className="text-lg font-bold text-slate-900">
                      Steps & Timeline
                    </h2>
                  </div>
                  {expandedSections.steps ? (
                    <ChevronUp className="w-5 h-5 text-slate-400" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-slate-400" />
                  )}
                </button>
                {expandedSections.steps && (
                  <div className="px-6 pb-6" data-testid="steps-content">
                    <div className="step-timeline">
                      {protocol.steps?.map((step) => (
                        <div key={`step-${step.step_number}`} className="step-item">
                          <div className="flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-4">
                            <div className="sm:w-40 flex-shrink-0">
                              <span className="text-xs font-mono text-orange-600 bg-orange-50 px-2 py-1 rounded">
                                {step.timeframe}
                              </span>
                            </div>
                            <div className="flex-1">
                              <h4 className="font-bold text-slate-900 mb-1">
                                Step {step.step_number}: {step.action}
                              </h4>
                              <p className="text-sm text-slate-500 mb-2">
                                Owner: {step.owner}
                              </p>
                              {step.artifacts?.length > 0 && (
                                <div className="flex flex-wrap gap-2">
                                  {step.artifacts.map((artifact, i) => (
                                    <span 
                                      key={`${step.step_number}-artifact-${i}`}
                                      className="text-xs px-2 py-1 bg-slate-100 text-slate-600 rounded"
                                    >
                                      {artifact}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Documentation Required */}
              <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
                <button
                  onClick={() => toggleSection("documentation")}
                  className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-slate-50"
                  data-testid="toggle-documentation"
                >
                  <div className="flex items-center gap-3">
                    <FileText className="w-5 h-5 text-slate-500" />
                    <h2 className="text-lg font-bold text-slate-900">
                      Required Documentation
                    </h2>
                  </div>
                  {expandedSections.documentation ? (
                    <ChevronUp className="w-5 h-5 text-slate-400" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-slate-400" />
                  )}
                </button>
                {expandedSections.documentation && (
                  <div className="px-6 pb-6" data-testid="documentation-content">
                    <ul className="space-y-2">
                      {protocol.required_documentation?.map((doc, index) => (
                        <li key={index} className="flex items-center gap-2 text-slate-600">
                          <Check className="w-4 h-4 text-emerald-500" />
                          {doc}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              {/* Data to Review */}
              <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
                <button
                  onClick={() => toggleSection("data")}
                  className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-slate-50"
                  data-testid="toggle-data"
                >
                  <div className="flex items-center gap-3">
                    <Database className="w-5 h-5 text-slate-500" />
                    <h2 className="text-lg font-bold text-slate-900">
                      Data to Review
                    </h2>
                  </div>
                  {expandedSections.data ? (
                    <ChevronUp className="w-5 h-5 text-slate-400" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-slate-400" />
                  )}
                </button>
                {expandedSections.data && (
                  <div className="px-6 pb-6" data-testid="data-content">
                    <ul className="space-y-2">
                      {protocol.data_to_review?.map((item, index) => (
                        <li key={index} className="flex items-center gap-2 text-slate-600">
                          <Check className="w-4 h-4 text-blue-500" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              {/* Implementation Notes */}
              {(protocol.implementation_notes || protocol.compliance_notes) && (
                <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
                  <button
                    onClick={() => toggleSection("notes")}
                    className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-slate-50"
                    data-testid="toggle-notes"
                  >
                    <h2 className="text-lg font-bold text-slate-900">
                      Implementation & Compliance Notes
                    </h2>
                    {expandedSections.notes ? (
                      <ChevronUp className="w-5 h-5 text-slate-400" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-slate-400" />
                    )}
                  </button>
                  {expandedSections.notes && (
                    <div className="px-6 pb-6 space-y-4" data-testid="notes-content">
                      {protocol.implementation_notes && (
                        <div>
                          <h3 className="font-bold text-slate-900 mb-2">
                            Implementation Notes
                          </h3>
                          <div 
                            className="text-slate-600 prose prose-sm max-w-none"
                            dangerouslySetInnerHTML={{ __html: sanitizeHtml(protocol.implementation_notes) }}
                          />
                        </div>
                      )}
                      {protocol.compliance_notes && (
                        <div>
                          <h3 className="font-bold text-slate-900 mb-2">
                            Compliance Notes
                          </h3>
                          <div 
                            className="text-slate-600 prose prose-sm max-w-none"
                            dangerouslySetInnerHTML={{ __html: sanitizeHtml(protocol.compliance_notes) }}
                          />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="sticky top-24 space-y-6">
                {/* PDF Download */}
                <div className="bg-orange-50 border border-orange-200 p-6 rounded-sm">
                  <h3 className="font-bold text-slate-900 mb-3 flex items-center gap-2">
                    <FileDown className="w-5 h-5 text-orange-500" />
                    Export Protocol
                  </h3>
                  <p className="text-sm text-slate-600 mb-4">
                    Download a formatted PDF version of this protocol for offline use or printing.
                  </p>
                  <Button
                    onClick={downloadPdf}
                    disabled={downloadingPdf}
                    className="w-full bg-orange-500 hover:bg-orange-600 text-white"
                    data-testid="download-pdf-sidebar-btn"
                  >
                    {downloadingPdf ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Generating PDF...
                      </>
                    ) : (
                      <>
                        <FileDown className="w-4 h-4 mr-2" />
                        Download PDF
                      </>
                    )}
                  </Button>
                </div>
                
                {/* Downloads - only show when assets exist */}
                {assets.length > 0 && (
                <div className="bg-white border border-slate-200 p-6 rounded-sm">
                  <h3 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
                    <Download className="w-5 h-5" />
                    Downloads
                  </h3>
                  <div className="space-y-3">
                    {assets.map((asset) => (
                      <a
                        key={asset.id}
                        href={asset.file_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => trackDownload(asset.id, asset.title)}
                        className="block p-3 bg-slate-50 hover:bg-slate-100 rounded transition-colors"
                        data-testid={`download-${asset.id}`}
                      >
                        <div className="flex items-center gap-3">
                          <FileText className="w-5 h-5 text-slate-500" />
                          <div className="flex-1">
                            <p className="text-sm font-medium text-slate-900">
                              {asset.title}
                            </p>
                            <p className="text-xs text-slate-500 uppercase">
                              {asset.file_type}
                            </p>
                          </div>
                        </div>
                      </a>
                    ))}
                  </div>
                </div>
                )}

                {/* Related */}
                <div className="bg-slate-50 border border-slate-200 p-6 rounded-sm">
                  <h3 className="font-bold text-slate-900 mb-4">Related Resources</h3>
                  <div className="space-y-3">
                    <Link to="/toolkits" className="block">
                      <Button variant="outline" className="w-full justify-start" data-testid="view-toolkits-sidebar">
                        View Toolkits
                      </Button>
                    </Link>
                    <Link to="/data" className="block">
                      <Button variant="outline" className="w-full justify-start" data-testid="data-guides-sidebar">
                        Data Guides
                      </Button>
                    </Link>
                    {pillar && (
                      <Link to={`/framework/${pillar.slug}`} className="block">
                        <Button variant="outline" className="w-full justify-start" data-testid="pillar-link-sidebar">
                          {pillar.title} Pillar
                        </Button>
                      </Link>
                    )}
                  </div>
                </div>

                {/* Tags */}
                {protocol.situation_tags?.length > 0 && (
                  <div className="bg-white border border-slate-200 p-6 rounded-sm">
                    <h3 className="font-bold text-slate-900 mb-4">Tags</h3>
                    <div className="flex flex-wrap gap-2">
                      {protocol.situation_tags.map((tag) => (
                        <span
                          key={tag}
                          className="text-xs px-3 py-1 bg-slate-100 text-slate-600 rounded-full"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
